'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle, Smartphone } from 'lucide-react';
import { formatPhoneInput, isValidLocalPhone, localPhoneValidationMessage } from '@/lib/phoneNumber';
import { getNextProxyBase } from '@/lib/nextProxy';
import { buildLaravelMountUrl, laravelPaths } from '@/lib/site';
import { resolvePostLoginRedirectWithUser } from '@/lib/authRedirect';
import { dispatchAuthStateChanged } from '@/lib/authSync';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'login' | 'register';
  onAuthSuccess?: () => void | Promise<void>;
}

export function AuthModal({ isOpen, onClose, initialTab = 'login', onAuthSuccess }: AuthModalProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(initialTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [csrfLoading, setCsrfLoading] = useState(false);
  const csrfInflightRef = useRef<Promise<string | null> | null>(null);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginProcessing, setLoginProcessing] = useState(false);
  const [loginError, setLoginError] = useState<Record<string, string>>({});
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');

  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirmation, setRegisterPasswordConfirmation] = useState('');
  const [registerProcessing, setRegisterProcessing] = useState(false);
  const [registerError, setRegisterError] = useState<Record<string, string>>({});

  const fetchLoggedInUserRole = async (): Promise<{
    role_id?: number;
    is_verified?: boolean | null;
    can_access_seller_dashboard?: boolean | null;
  } | null> => {
    const nx = getNextProxyBase();
    const res = await fetchWithTimeout(`${nx}/auth/user`, { credentials: 'include', cache: 'no-store' }, 8000);
    if (!res.ok) return null;
    const data = (await res.json().catch(() => ({}))) as {
      user?: {
        role_id?: number;
        is_verified?: boolean | null;
        can_access_seller_dashboard?: boolean | null;
      } | null;
    };
    return data?.user ?? null;
  };

  const goAfterLogin = async (redirect: unknown) => {
    const previousPageUrl = typeof window !== 'undefined' ? window.location.href : undefined;
    const target = await resolvePostLoginRedirectWithUser(redirect, fetchLoggedInUserRole, previousPageUrl);
    window.location.href = target;
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    setActiveTab(initialTab);
    setLoginError({});
    setRegisterError({});
    setSocialError(null);
    setLoginMethod('email');
    setOtpSent(false);
    setCountdown(0);
    setLoginPhone('');
    setLoginOtp('');
    setCsrfToken(null);
    void getCsrfToken(true);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, initialTab]);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = window.setTimeout(() => setCountdown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [countdown]);

  if (!isOpen) return null;

  const nx = getNextProxyBase();

  const fetchWithTimeout = async (input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const getCsrfToken = async (force = false): Promise<string | null> => {
    if (!force && csrfToken) return csrfToken;
    if (!force && csrfInflightRef.current) return csrfInflightRef.current;
    if (force) csrfInflightRef.current = null;

    const promise = (async () => {
      setCsrfLoading(true);
      try {
        const r = await fetchWithTimeout(`${nx}/auth/csrf`, { credentials: 'include' }, 8000);
        if (!r.ok) return null;
        const data = await r.json();
        const token = data?.token ?? null;
        setCsrfToken(token);
        return token;
      } catch {
        return null;
      } finally {
        setCsrfLoading(false);
        csrfInflightRef.current = null;
      }
    })();

    csrfInflightRef.current = promise;
    return promise;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError({});
    setLoginProcessing(true);
    try {
      const requestLogin = async (token: string | null) =>
        fetchWithTimeout(`${nx}/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-TOKEN': token } : {}),
          },
          body: JSON.stringify({
            _token: token,
            email: loginEmail,
            password: loginPassword,
            remember: true,
          }),
        });

      let token = await getCsrfToken();
      let res = await requestLogin(token);
      if (res.status === 419) {
        token = await getCsrfToken(true);
        res = await requestLogin(token);
      }
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (typeof window !== 'undefined') {
          (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = true;
        }
        dispatchAuthStateChanged();
        onClose();
        if (onAuthSuccess) {
          await onAuthSuccess();
          return;
        }
        await goAfterLogin(data.redirect);
        return;
      }
      if (data.errors && typeof data.errors === 'object') {
        setLoginError(data.errors as Record<string, string>);
      } else {
        setLoginError({ email: data.message || 'Invalid credentials. Please try again.' });
      }
    } catch {
      setLoginError({ email: 'Unable to reach server. You can log in on the main site.' });
    } finally {
      setLoginProcessing(false);
    }
  };

  const mapApiErrors = (data: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {};
    const errs = data.errors;
    if (errs && typeof errs === 'object' && errs !== null) {
      for (const [k, v] of Object.entries(errs as Record<string, unknown>)) {
        out[k] = Array.isArray(v) ? String(v[0]) : String(v);
      }
    }
    return out;
  };

  const omitLoginErrors = (keys: string[]) => {
    setLoginError((prev) => {
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  };

  const omitRegisterErrors = (keys: string[]) => {
    setRegisterError((prev) => {
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  };

  const sendOtpRequest = async () => {
    setLoginError({});
    if (!isValidLocalPhone(loginPhone)) {
      setLoginError({ phone: localPhoneValidationMessage() });
      return;
    }
    setLoginProcessing(true);
    try {
      const requestOtpSend = async (token: string | null) =>
        fetchWithTimeout(`${nx}/auth/login/otp/send`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-TOKEN': token } : {}),
          },
          body: JSON.stringify({ _token: token, phone: loginPhone }),
        });

      let token = await getCsrfToken();
      let res = await requestOtpSend(token);
      if (res.status === 419) {
        token = await getCsrfToken(true);
        res = await requestOtpSend(token);
      }
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (res.ok) {
        setOtpSent(true);
        setLoginOtp('');
        setCountdown(60);
        return;
      }
      const mapped = mapApiErrors(data);
      setLoginError(Object.keys(mapped).length ? mapped : { phone: String(data.message || 'Failed to send OTP.') });
      setOtpSent(false);
      setCountdown(0);
    } catch {
      setLoginError({ phone: 'Failed to send OTP. Please try again.' });
      setOtpSent(false);
      setCountdown(0);
    } finally {
      setLoginProcessing(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginOtp.length >= 6) {
      await handleOtpLogin(e);
      return;
    }
    if (otpSent) {
      await handleOtpLogin(e);
      return;
    }
    await handleSendOtp(e);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtpRequest();
  };

  const handleOtpLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginOtp.length < 6) {
      setLoginError({ otp: 'Please enter the verification code.' });
      return;
    }
    setLoginError({});
    setLoginProcessing(true);
    try {
      const requestOtpLogin = async (token: string | null) =>
        fetchWithTimeout(`${nx}/auth/login/otp`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-TOKEN': token } : {}),
          },
          body: JSON.stringify({ _token: token, phone: loginPhone, otp: loginOtp }),
        });

      let token = await getCsrfToken();
      let res = await requestOtpLogin(token);
      if (res.status === 419) {
        token = await getCsrfToken(true);
        res = await requestOtpLogin(token);
      }
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown> & { redirect?: string };
      if (res.ok) {
        if (typeof window !== 'undefined') {
          (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = true;
        }
        dispatchAuthStateChanged();
        onClose();
        if (onAuthSuccess) {
          await onAuthSuccess();
          return;
        }
        await goAfterLogin(data.redirect);
        return;
      }
      const mapped = mapApiErrors(data);
      setLoginError(Object.keys(mapped).length ? mapped : { otp: String(data.message || 'Login failed.') });
    } catch {
      setLoginError({ otp: 'Unable to reach server. You can log in on the main site.' });
    } finally {
      setLoginProcessing(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) return;
    setRegisterError({});
    if (registerPhone.trim() && !isValidLocalPhone(registerPhone)) {
      setRegisterError({ phone: localPhoneValidationMessage() });
      return;
    }
    setRegisterProcessing(true);
    try {
      const requestRegister = async (token: string | null) =>
        fetchWithTimeout(`${nx}/auth/register`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-TOKEN': token } : {}),
          },
          body: JSON.stringify({
            _token: token,
            name: registerName,
            email: registerEmail,
            phone: registerPhone.trim() ? formatPhoneInput(registerPhone) : undefined,
            password: registerPassword,
            password_confirmation: registerPasswordConfirmation,
          }),
        });

      let token = await getCsrfToken();
      let res = await requestRegister(token);
      if (res.status === 419) {
        token = await getCsrfToken(true);
        res = await requestRegister(token);
      }
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (typeof window !== 'undefined') {
          (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = true;
        }
        dispatchAuthStateChanged();
        onClose();
        if (onAuthSuccess) {
          await onAuthSuccess();
          return;
        }
        window.location.href = laravelPaths.dashboard();
        return;
      }
      if (data.errors && typeof data.errors === 'object') {
        const errs: Record<string, string> = {};
        for (const [k, v] of Object.entries(data.errors)) {
          errs[k] = Array.isArray(v) ? (v[0] as string) : String(v);
        }
        setRegisterError(errs);
      } else {
        setRegisterError({ email: data.message || 'Registration failed. Please try again.' });
      }
    } catch {
      setRegisterError({ email: 'Unable to reach server. You can register on the main site.' });
    } finally {
      setRegisterProcessing(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'facebook') => {
    setSocialError(null);
    window.location.href = buildLaravelMountUrl(`/auth/${provider}/redirect`);
  };

  const handleForgotPassword = () => {
    onClose();
    window.location.href = buildLaravelMountUrl('/forgot-password');
  };

  const termsUrl = laravelPaths.terms();
  const privacyUrl = laravelPaths.privacy();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 md:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div
        className="bg-white dark:bg-neutral-800 rounded-lg md:rounded-xl shadow-2xl max-w-md w-full max-h-[95vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 md:top-4 right-3 md:right-4 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition-colors z-10"
          aria-label="Close"
        >
          <X size={16} className="md:w-[18px] md:h-[18px]" />
        </button>

        <div className="p-4 md:p-5">
          <h2 id="auth-modal-title" className="text-lg md:text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-3 md:mb-4 pr-6 md:pr-8">
            Welcome Back
          </h2>

          {/* Tabs */}
          <div className="flex bg-neutral-100 dark:bg-neutral-700 rounded-lg p-0.5 md:p-1 mb-3 md:mb-4">
            <button
              type="button"
              onClick={() => {
                setActiveTab('login');
                setLoginError({});
              }}
              className={`flex-1 py-1.5 md:py-2 text-xs md:text-sm font-medium transition-all rounded-md ${activeTab === 'login' ? 'bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('register');
                setRegisterError({});
              }}
              className={`flex-1 py-1.5 md:py-2 text-xs md:text-sm font-medium transition-all rounded-md ${activeTab === 'register' ? 'bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
            >
              Register
            </button>
          </div>

          {socialError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-red-600 dark:text-red-400">{socialError}</p>
            </div>
          )}

          {/* Social Login */}
          <div className="space-y-2 md:space-y-2.5 mb-3 md:mb-4">
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              className="w-full flex items-center justify-center gap-2 md:gap-3 py-2.5 md:py-3 px-3 md:px-4 border border-neutral-200 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="text-xs md:text-sm font-medium text-neutral-700 dark:text-neutral-300">Continue with Google</span>
            </button>
            {/* Facebook login hidden for now — re-enable when OAuth is ready
            <button
              type="button"
              onClick={() => handleSocialLogin('facebook')}
              className="w-full flex items-center justify-center gap-2 md:gap-3 py-2.5 md:py-3 px-3 md:px-4 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors font-medium"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 fill-white shrink-0" viewBox="0 0 24 24" aria-hidden>
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="text-xs md:text-sm">Continue with Facebook</span>
            </button>
            */}
          </div>

          <div className="relative my-3 md:my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200 dark:border-neutral-600" />
            </div>
            <div className="relative flex justify-center text-[10px] md:text-xs">
              <span className="px-2 md:px-3 bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 uppercase font-medium">OR CONTINUE WITH EMAIL</span>
            </div>
          </div>

          {activeTab === 'login' && (
            <>
              <div className="flex border-b border-neutral-200 dark:border-neutral-700 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('email');
                    setOtpSent(false);
                    setCountdown(0);
                    setLoginPhone('');
                    setLoginOtp('');
                    setLoginError({});
                  }}
                  className={`flex-1 py-3 text-xs md:text-sm font-medium transition-colors border-b-2 ${
                    loginMethod === 'email'
                      ? 'text-brand-orange border-brand-orange'
                      : 'text-neutral-500 dark:text-neutral-400 border-transparent hover:text-neutral-700 dark:hover:text-neutral-300'
                  }`}
                >
                  <Mail size={16} className="inline-block mr-2 align-text-bottom" />
                  Email Login
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('phone');
                    setLoginError({});
                    setLoginEmail('');
                    setLoginPassword('');
                  }}
                  className={`flex-1 py-3 text-xs md:text-sm font-medium transition-colors border-b-2 ${
                    loginMethod === 'phone'
                      ? 'text-brand-orange border-brand-orange'
                      : 'text-neutral-500 dark:text-neutral-400 border-transparent hover:text-neutral-700 dark:hover:text-neutral-300'
                  }`}
                >
                  <Smartphone size={16} className="inline-block mr-2 align-text-bottom" />
                  Phone OTP
                </button>
              </div>

              {loginMethod === 'email' && (
                <form onSubmit={handleLogin} className="space-y-2.5 md:space-y-3">
                  <div>
                    <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">EMAIL ADDRESS</label>
                    <div className="relative">
                      <Mail className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => {
                          setLoginEmail(e.target.value);
                          omitLoginErrors(['email']);
                        }}
                        placeholder="name@example.com"
                        className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${loginError.email ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                      />
                    </div>
                    {loginError.email && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {loginError.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">PASSWORD</label>
                    <div className="relative">
                      <Lock className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${loginError.password ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                      />
                    </div>
                    {loginError.password && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {loginError.password}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end -mt-1">
                    <button type="button" onClick={handleForgotPassword} className="text-[10px] md:text-xs text-brand-orange hover:underline font-medium">
                      Forgot Password?
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={loginProcessing}
                    className="w-full py-2 md:py-2.5 bg-brand-orange text-white rounded-lg font-semibold hover:bg-brand-orange-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm mt-2"
                  >
                    {loginProcessing ? <Loader2 size={16} className="animate-spin" /> : <>Sign In <ArrowRight size={14} className="md:w-4 md:h-4" /></>}
                  </button>
                </form>
              )}

              {loginMethod === 'phone' && (
                <form onSubmit={handlePhoneSubmit} className="space-y-2.5 md:space-y-3">
                  <div>
                    <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">PHONE NUMBER</label>
                    <div className="relative">
                      <Smartphone className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                      <input
                        type="text"
                        value={loginPhone}
                        onChange={(e) => {
                          setLoginPhone(formatPhoneInput(e.target.value));
                          omitLoginErrors(['phone']);
                        }}
                        placeholder="0771234567"
                        maxLength={10}
                        inputMode="numeric"
                        disabled={otpSent}
                        className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${loginError.phone ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'} ${otpSent ? 'bg-neutral-100 dark:bg-neutral-800 cursor-not-allowed' : ''}`}
                      />
                    </div>
                    {loginError.phone && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {loginError.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">ENTER OTP</label>
                    <div className="relative">
                      <Lock className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                      <input
                        type="text"
                        autoComplete="one-time-code"
                        value={loginOtp}
                        onChange={(e) => {
                          setLoginOtp(e.target.value.slice(0, 64));
                          omitLoginErrors(['otp']);
                        }}
                        placeholder="XXXXXX"
                        className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${loginError.otp ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                      />
                    </div>
                    {loginError.otp && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {loginError.otp}
                      </p>
                    )}
                  </div>

                  {otpSent && (
                    <div>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-[10px] md:text-xs text-neutral-500 dark:text-neutral-400">
                          {countdown > 0 ? (
                            <span>
                              Resend code in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
                            </span>
                          ) : (
                            <button type="button" onClick={() => void sendOtpRequest()} className="text-brand-orange hover:underline font-medium">
                              Resend Code
                            </button>
                          )}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setOtpSent(false);
                            setCountdown(0);
                            setLoginOtp('');
                            setLoginError({});
                          }}
                          className="text-[10px] md:text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                        >
                          Change Number
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loginProcessing}
                    className="w-full py-2 md:py-2.5 bg-brand-orange text-white rounded-lg font-semibold hover:bg-brand-orange-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm mt-2"
                  >
                    {loginProcessing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        {loginOtp.length >= 6 || otpSent ? 'Verify & Login' : 'Send OTP'} <ArrowRight size={14} className="md:w-4 md:h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}

          {activeTab === 'register' && (
            <form onSubmit={handleRegister} className="space-y-2.5 md:space-y-3">
              <div>
                <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">FULL NAME</label>
                <input
                  type="text"
                  value={registerName}
                  onChange={(e) => setRegisterName(e.target.value)}
                  placeholder="John Doe"
                  className={`w-full pl-3 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${registerError.name ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                />
                {registerError.name && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {registerError.name}</p>}
              </div>
              <div>
                <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">EMAIL ADDRESS</label>
                <div className="relative">
                  <Mail className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                  <input
                    type="email"
                    value={registerEmail}
                    onChange={(e) => {
                      setRegisterEmail(e.target.value);
                      omitRegisterErrors(['email']);
                    }}
                    placeholder="name@example.com"
                    className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${registerError.email ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                  />
                </div>
                {registerError.email && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {registerError.email}</p>}
              </div>
              <div>
                <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">
                  PHONE NUMBER <span className="normal-case font-normal text-neutral-400 dark:text-neutral-500">(Optional)</span>
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                  <input
                    type="tel"
                    value={registerPhone}
                    onChange={(e) => {
                      setRegisterPhone(formatPhoneInput(e.target.value));
                      omitRegisterErrors(['phone']);
                    }}
                    placeholder="0771234567"
                    maxLength={10}
                    inputMode="numeric"
                    autoComplete="tel"
                    className={`w-full pl-8 md:pl-9 pr-3 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${registerError.phone ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                  />
                </div>
                {registerError.phone && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {registerError.phone}</p>}
              </div>
              <div>
                <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">PASSWORD</label>
                <div className="relative">
                  <Lock className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={registerPassword}
                    onChange={(e) => {
                      setRegisterPassword(e.target.value);
                      omitRegisterErrors(['password', 'password_confirmation']);
                    }}
                    placeholder="••••••••"
                    className={`w-full pl-8 md:pl-9 pr-9 md:pr-10 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${registerError.password ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 p-0.5">
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {registerError.password && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {registerError.password}</p>}
              </div>
              <div>
                <label className="block text-[10px] md:text-xs font-semibold text-neutral-700 dark:text-neutral-400 mb-1 md:mb-1.5 uppercase tracking-wide">CONFIRM PASSWORD</label>
                <div className="relative">
                  <Lock className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={14} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={registerPasswordConfirmation}
                    onChange={(e) => {
                      setRegisterPasswordConfirmation(e.target.value);
                      omitRegisterErrors(['password_confirmation', 'password']);
                    }}
                    placeholder="••••••••"
                    className={`w-full pl-8 md:pl-9 pr-9 md:pr-10 py-2 md:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-xs md:text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 ${registerError.password_confirmation ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-700'}`}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 p-0.5">
                    {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {registerError.password_confirmation && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {registerError.password_confirmation}</p>}
              </div>
              <label className="flex items-start gap-1.5 md:gap-2 cursor-pointer mt-2">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="w-3.5 h-3.5 md:w-4 md:h-4 mt-0.5 rounded border-neutral-300 text-brand-orange focus:ring-brand-orange shrink-0" />
                <span className="text-[10px] md:text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  By joining, you agree to BuyMe.lk&apos;s <a href={termsUrl} className="text-brand-orange hover:underline" target="_blank" rel="noopener noreferrer">Terms</a> and <a href={privacyUrl} className="text-brand-orange hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </span>
              </label>
              <button
                type="submit"
                disabled={registerProcessing || !agreedToTerms}
                className="w-full py-2 md:py-2.5 bg-brand-orange text-white rounded-lg font-semibold hover:bg-brand-orange-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm mt-2"
              >
                {registerProcessing ? <Loader2 size={16} className="animate-spin" /> : <>Sign Up <ArrowRight size={14} className="md:w-4 md:h-4" /></>}
              </button>
            </form>
          )}

          <p className="text-[10px] md:text-xs text-neutral-500 dark:text-neutral-400 text-center mt-3 md:mt-4 leading-relaxed">
            By joining, you agree to BuyMe.lk&apos;s <a href={termsUrl} className="text-brand-orange hover:underline" target="_blank" rel="noopener noreferrer">Terms</a> and <a href={privacyUrl} className="text-brand-orange hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
